import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { combineLatest, Observable, Subject, forkJoin, of } from 'rxjs';
import { take, takeUntil, tap, finalize, switchMap, skip, filter } from 'rxjs/operators';
import { Job, JobSearchQuery, JobService } from '@intranet/modules/jobs';
import { Client, LoginService, TogglService } from '../..';
import { PageEvent } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { AlertDialog } from '@intranet/shared/alert/alert-dialog.component';
import { Router, ActivatedRoute } from '@angular/router';
import { HotkeyService } from 'src/app/core/services/hotkey.service';
import { UserService } from '@intranet/modules/users/user.service';
import { User, UserJobChoice } from '@intranet/modules/users/user.types';
import { JobStatus } from '@intranet/modules/jobs/job.types';

@Component({
    selector: 'jobs-listing',
    templateUrl: './jobs-listing.component.html',
    styleUrls: ['./jobs-listing.component.scss'],
})
export class JobsListingComponent implements OnInit, OnDestroy {
    @Input() hideSearch = false;
    @Input() hideTabs = false;
    @Input() hideHeader = false;
    @Input() showAll: boolean = false;
    @Input() clients: Client[] = [];
    @Input() isGroup: boolean = false;

    isInvokedViaRouter = false;
    isFirstQuery = true;
    ownerFromUrl = false;
    isLoading: boolean = false;
    isOwnersLoading: boolean = true;
    userId: string = '';
    error: any;
    showArchived: boolean = false;
    showDialog = false;
    currentJobs: boolean;
    jobs: Job[] = [];
    jobId: number;
    filterQuery: JobSearchQuery;
    totalResults = 0;
    pinnedJobs: UserJobChoice[] = [];
    pinnedJobIds: number[] = [];
    selectedJobs: Job[] = [];
    availableOwners: User[] = [];
    private billableInFlight = new Set<number>();

    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        protected dialog: MatDialog,
        private _jobService: JobService,
        private _loginService: LoginService,
        private togglService: TogglService,
        private router: Router,
        private _activatedRoute: ActivatedRoute,
        private hotkeyService: HotkeyService,
        private userService: UserService
    ) {}

    ngOnInit(): void {
        // Register hotkey for "Add a new Job"
        this.hotkeyService.registerHotkey(
            'ctrl+shift+a',
            'Add a new job',
            () => this.addNewJob()
        );

        if (!this.showAll) {
            this.userId = this._loginService.userId;
        }

        this.isInvokedViaRouter = this.constructor === this._activatedRoute.component;

        this._activatedRoute.queryParamMap.subscribe((params) => {
            if (this.isInvokedViaRouter) {
                const owner = params.get('owner');
                const clientId = params.get('clientId');
                const searchTerm = params.get('searchTerm');
                const activeparam = params.get('active');
                const active = activeparam === 'Active' ? 'Active' : activeparam === 'Inactive' ? 'Inactive' : 'All';

                // Track if we need to update URL with default owner
                let updateUrlWithOwner = false;

                if (owner === 'all') {
                    // Explicitly set to "All" via URL
                    this.userId = '';
                    this.ownerFromUrl = true;
                } else if (owner) {
                    // Specific owner set via URL
                    this.userId = owner;
                    this.ownerFromUrl = true;
                } else if (!this.showAll) {
                    // No owner param - fresh navigation, default to current user
                    this.userId = this._loginService.userId;
                    this.ownerFromUrl = false;
                    updateUrlWithOwner = true;
                } else {
                    this.userId = '';
                    this.ownerFromUrl = false;
                }

                this.filterQuery = {
                    page: 1,
                    sortBy: 'type',
                    sortDir: 'desc',
                    clients: this.clients ? this.clients.map((c) => c.clientId) : [],
                    userId: this.userId,
                    clientId: clientId ? +clientId : null,
                    searchTerm: searchTerm || '',
                    active: active || 'All',
                };

                // Update URL with default owner (without adding history entry)
                if (updateUrlWithOwner) {
                    this.router.navigate([], {
                        relativeTo: this._activatedRoute,
                        queryParams: {
                            owner: this.userId,
                            clientId: this.filterQuery.clientId || null,
                            searchTerm: this.filterQuery.searchTerm || null,
                        },
                        queryParamsHandling: 'merge',
                        replaceUrl: true,
                    });
                }

                this.isLoading = true;
                this.loadJobs(this.filterQuery).subscribe({
                    complete: () => {
                        this.isLoading = false;
                        this.isFirstQuery = false;
                    },
                });
            }
        });

        if (!this.isInvokedViaRouter) {
            this.filterQuery = {
                page: 1,
                sortBy: 'type',
                sortDir: 'desc',
                clients: this.clients ? this.clients.map(c => c.clientId) : [],
                userId: this.userId,
                active: 'All',
                clientId: null,
            };

            // Immediately load jobs using the initialized filterQuery.
            this.onFilter(this.filterQuery);
        }

        if (!this.hideSearch) {
            combineLatest([this._jobService.filterQuery$])
                .pipe(
                    take(1),
                    tap(([filterQuery]) => {
                        if (!this.isInvokedViaRouter) {
                            this.filterQuery = {
                                ...filterQuery,
                                clients: this.clients.map((c) => c.clientId),
                            };
                        }
                    })
                )
                .subscribe({
                    complete: () => {
                        //this.onFilter(this.filterQuery);
                    },
                });

            this._jobService.filterQuery$
                .pipe(
                    takeUntil(this._unsubscribeAll),
                    tap((filterQuery) => {
                        this.filterQuery = filterQuery;
                    })
                )
                .subscribe();
        }

        this._jobService.jobs$
            .pipe(
                takeUntil(this._unsubscribeAll),
                tap((response) => {
                    this.jobs = response;
                })
            )
            .subscribe();

        this._jobService.jobsTotalResults$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (jobActions) => {
                    this.totalResults = jobActions;
                },
            });

        this.userService.getUserJobsList().subscribe({
            next: (jobs) => {
                this.pinnedJobs = jobs;
                this.pinnedJobIds = jobs.map(j => j.jobId);
            }
        });

        this._jobService.billableStatusChanged$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ jobId, isBillable }) => {
                this.jobs = this.jobs.map(j =>
                    j.jobId === jobId ? { ...j, isBillable } : j
                );
                this.selectedJobs = this.selectedJobs.map(j =>
                    j.jobId === jobId ? { ...j, isBillable } : j
                );
            });

        if (this.clients && this.clients.length > 0) {
            this.loadAvailableOwners();
        }
    }

    private loadAvailableOwners(): void {
        const clientIds = this.clients.map(c => c.clientId);
        combineLatest([
            this._jobService.getJobOwners(undefined, clientIds),
            this.userService.users$
        ]).pipe(
            take(1)
        ).subscribe({
            next: ([ownerResult, allUsers]: [{ userIds: string[] }, User[]]) => {
                const ownerIds = new Set<string>(ownerResult.userIds);

                const userMap = new Map<string, User>();
                allUsers.forEach(u => userMap.set(u.id, u));

                this.availableOwners = Array.from(ownerIds)
                    .map(id => userMap.get(id))
                    .filter((u): u is User => u !== undefined);
            }
        });
    }

    ngOnDestroy() {
        this._unsubscribeAll.next('');
        this._unsubscribeAll.complete();
        this._jobService.reset();
    }

    // Call the API via the job service
    loadJobs(query?: JobSearchQuery): Observable<Job[]> {
        this.isLoading = true;
        return this._jobService.getJobs({
            ...query,
            archived: this.showArchived,
        });
    }

    // Called by the main filters
    onFilter(filterQuery: JobSearchQuery, keepPage: boolean = false) {
        if (this.isInvokedViaRouter) {
            this.router.navigate([], {
                relativeTo: this._activatedRoute,
                queryParams: {
                    owner: filterQuery.userId || 'all',
                    clientId: filterQuery.clientId || null,
                    searchTerm: filterQuery.searchTerm || null,
                    active: filterQuery.active || 'All',
                },
                queryParamsHandling: 'merge',
            });
        } else {
            const page = keepPage && this.filterQuery?.page
                ? this.filterQuery.page
                : 1;

            const query: JobSearchQuery = {
                ...filterQuery,
                clients: this.clients.map((c) => c.clientId),
                page,
                isPaginating: keepPage,
            };

            this.filterQuery = query;
            this.isLoading = true;

            this.loadJobs(query).subscribe({
                complete: () => (this.isLoading = false),
            });
        }
    }

    onPaginate(e: PageEvent) {
        const query: JobSearchQuery = {
            ...this.filterQuery,
            page: e.pageIndex + 1,
            isPaginating: true,
        };

        this.filterQuery = query;

        this.loadJobs(query).subscribe({
            complete: () => {
                this.isLoading = false;
                const pageEl = document.getElementById('page');
                if (pageEl) {
                    pageEl.scrollTo(0, 0);
                }
            }
        });
    }

    onArchived({ job, error }: { job: Job, error: string }) {
        if (error) {
            this.dialog.open(AlertDialog, {
                width: '320px',
                data: {
                    title: 'Cannot archive job',
                    message: error,
                    confirm: true,
                    confirmTitle: 'OK',
                },
            });
        } else {
            this.isLoading = true;
            this.loadJobs({ ...this.filterQuery }).subscribe({
                complete: () => (this.isLoading = false),
            });
        }
    }

    onDeleteJob(job: Job) {
        const dialogRef = this.dialog.open(AlertDialog, {
            width: '320px',
            data: {
                title: 'Are you sure?',
                message: `You are about to delete this job. This operation cannot be undone. Are you sure?`,
                confirm: true,
                confirmTitle: 'Yes',
            },
        });

        dialogRef
            .afterClosed()
            .pipe(
                switchMap((result) => {
                    if (!result) {
                        return of(null);
                    }
                    return this._jobService.deleteJob(job.jobId);
                }),
                tap(response => {
                    if (response && response.error) {
                        this.dialog.open(AlertDialog, {
                            width: '320px',
                            data: {
                                title: 'Cannot delete job',
                                message: response.error,
                                confirm: true,
                                confirmTitle: 'OK',
                            },
                        });
                    } else {
                        this.onArchived({ job, error: '' });
                    }
                })
            )
            .subscribe();
    }

    onSort(prop: string) {
        this.isLoading = true;

        const { sortBy: prevSortBy, sortDir: prevSortDir } = this.filterQuery;

        this.loadJobs({
            ...this.filterQuery,
            sortBy: prop,
            sortDir:
                prop === prevSortBy
                    ? prevSortDir === 'asc'
                        ? 'desc'
                        : 'asc'
                    : 'asc',
        }).subscribe({
            complete: () => (this.isLoading = false),
        });
    }

    onJobManageChanged() {
        this.showDialog = false;
        const selectedIds = this.selectedJobs.map(j => j.jobId);
        this.onFilter(this.filterQuery, true);

        this._jobService.jobs$
            .pipe(
                skip(1),     // skip the value you already loaded
                take(1),
                tap((freshJobs) => {
                    this.selectedJobs = freshJobs.filter(j => selectedIds.includes(j.jobId));
                })
            )
            .subscribe();
    }

    // Gets status from tabs and sets the listing ID
    onStatus(status: string): void {
        this.showArchived = status === 'archived';

        const query: JobSearchQuery = {
            ...this.filterQuery,
            page: 1,
            isPaginating: false,
        };

        this.filterQuery = query;

        this.loadJobs(query).subscribe({
            complete: () => (this.isLoading = false),
        });
    }

    onClickToggle(toggle: boolean): void {
        this.jobId = null;
        this.showDialog = toggle;
    }

    addNewJob() {
        this.onClickToggle(true);
        // if (this.togglService.isLoggedIn()) {
        //     this.onClickToggle(true);
        // } else {
        // this.router.navigateByUrl('/users/settings');
        // }
    }

    onEdit(job) {
        this.jobId = job.jobId;
        this.showDialog = true;
    }

    onOwnersLoading(loading: boolean) {
        this.isOwnersLoading = loading;
    }

    handlePin(job: Job): void {
        this.userService.addJobsToUserSelection([job.jobId]).subscribe({
            next: (res) => {
                if (res.success) {
                    this.pinnedJobs.push({
                        jobId: job.jobId,
                        jobName: job.jobTitle,
                        jobNumber: job.jobNumber,
                        clientId: job.clientId,
                        clientName: job.client.clientName,
                    });
                    this.pinnedJobIds.push(job.jobId);
                }
            }
        });
    }

    handleUnpin(job: Job): void {
        this.userService.removeJobsFromUserSelection([job.jobId]).subscribe({
            next: (res) => {
                if (res.success) {
                    this.pinnedJobs = this.pinnedJobs.filter(j => j.jobId !== job.jobId);
                    this.pinnedJobIds = this.pinnedJobIds.filter(id => id !== job.jobId);
                }
            }
        });
    }

    handleBulk(action: string, jobs: Job[]) {
        // if it's archive/unarchive, confirm first
        if (action === 'archived' || action === 'live') {
            const verb = action === 'archived' ? 'archive' : 'un-archive';
            const noun = jobs.length > 1 ? 'these jobs' : 'this job';
            const dialogRef = this.dialog.open(AlertDialog, {
                width: '320px',
                data: {
                    title: 'Are you sure?',
                    message: `You are about to ${verb} ${noun}. Are you sure?`,
                    confirm: true,
                    confirmTitle: 'Yes',
                }
            });

            dialogRef.afterClosed().pipe(
                take(1),
                filter(confirmed => !!confirmed)
            ).subscribe(() => this.runBulk(action, jobs));
        }
        else {
            // for billable/non-billable, just run the bulk action directly
            this.runBulk(action, jobs);
        }
    }

    // This method handles the bulk action for jobs
    private runBulk(action: string, jobs: Job[]) {
        const isBillableAction = action === 'billable' || action === 'non-billable';

        // Optimistic UI
        if (isBillableAction) {
            const makeBillable = action === 'billable';
            this.jobs = this.jobs.map(j =>
                jobs.some(bj => bj.jobId === j.jobId)
                    ? { ...j, isBillable: makeBillable }
                    : j
            );
        } else {
            this.isLoading = true;
            const setArchived = action === 'archived';
            this.jobs = this.jobs.map(j =>
                jobs.some(bj => bj.jobId === j.jobId)
                    ? { ...j, archive: setArchived, jobStatus: setArchived ? JobStatus.Archived : JobStatus.Live }
                    : j
            );
        }

        // Billable actions use the lightweight dedicated endpoint
        if (isBillableAction) {
            const makeBillable = action === 'billable';
            const calls$ = jobs.map(j =>
                this._jobService.changeBillableStatus(j.jobId, makeBillable)
            );
            forkJoin(calls$).subscribe({
                next: () => {
                    jobs.forEach(j => this._jobService.notifyBillableChanged(j.jobId, makeBillable));
                    this.selectedJobs = [];
                },
                error: err => {
                    console.error('Bulk update failed', err);
                    this.onFilter(this.filterQuery, true);
                    this.selectedJobs = [];
                    this.dialog.open(AlertDialog, {
                        width: '320px',
                        data: {
                            title: 'Bulk update failed',
                            message: err?.message || 'Something went wrong. Please try again.',
                            confirm: true,
                            confirmTitle: 'OK',
                        },
                    });
                }
            });
            return;
        }

        // Archive/live actions still need the full job update
        const calls$ = jobs.map(j =>
            this._jobService.getJob(j.jobId).pipe(
                take(1),
                switchMap(fullJob => {
                    fullJob.jobTypeId = fullJob.jobType?.jobTypeId ?? fullJob.jobTypeId;
                    fullJob.archive = (action === 'archived');
                    fullJob.jobStatus = fullJob.archive ? JobStatus.Archived : JobStatus.Live;
                    return this._jobService.addOrUpdateJob(fullJob);
                })
            )
        );

        forkJoin(calls$)
            .pipe(finalize(() => {
                this.isLoading = false;
                this.onFilter(this.filterQuery, true);
                this.selectedJobs = [];
            }))
            .subscribe({
                next: () => {},
                error: err => {
                    console.error('Bulk update failed', err);
                    this.onFilter(this.filterQuery, true);
                    this.selectedJobs = [];
                    this.dialog.open(AlertDialog, {
                        width: '320px',
                        data: {
                            title: 'Bulk update failed',
                            message: err?.message || 'Something went wrong. Please try again.',
                            confirm: true,
                            confirmTitle: 'OK',
                        },
                    });
                }
            });
    }

    handleToggleBillable(job: Job) {
        if (this.billableInFlight.has(job.jobId)) {
            return;
        }

        const makeBillable = !job.isBillable;

        this.billableInFlight.add(job.jobId);

        // Optimistic UI: flip isBillable in both jobs and selectedJobs
        this.jobs = this.jobs.map(j =>
            j.jobId === job.jobId ? { ...j, isBillable: makeBillable } : j
        );
        this.selectedJobs = this.selectedJobs.map(j =>
            j.jobId === job.jobId ? { ...j, isBillable: makeBillable } : j
        );

        this._jobService.changeBillableStatus(job.jobId, makeBillable).pipe(
            finalize(() => this.billableInFlight.delete(job.jobId))
        ).subscribe({
            next: () => {
                this._jobService.notifyBillableChanged(job.jobId, makeBillable);
            },
            error: err => {
                console.error(err);

                // rollback on error
                this.jobs = this.jobs.map(j =>
                    j.jobId === job.jobId ? { ...j, isBillable: !makeBillable } : j
                );
                this.selectedJobs = this.selectedJobs.map(j =>
                    j.jobId === job.jobId ? { ...j, isBillable: !makeBillable } : j
                );

                this.dialog.open(AlertDialog, {
                    width: '320px',
                    data: {
                        title: 'Update failed',
                        message: err?.message || 'Something went wrong. Please try again.',
                        confirm: true,
                        confirmTitle: 'OK',
                    },
                });
            }
        });
    }
}
